import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { ClaroStaffInvites } from "../components/admin/ClaroStaffInvites";
import { AccessReport } from "../components/admin/AccessReport";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import EntityProfile from "../components/EntityProfile";
import CipImpact from "../components/CipImpact";
import RsawTemplates from "../components/RsawTemplates";
import { Plus, Trash, Warning, Download, UploadSimple, Scales, PenNib, ShieldCheck, PencilSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { PasswordChecklist, passwordValid, generatePassword, usePasswordPolicy } from "../components/PasswordStrength";
import RetentionManager from "../components/RetentionManager";
import AiUsageReport from "../components/AiUsageReport";
import ApprovalRoutes from "../components/ApprovalRoutes";
import Registrations from "../components/Registrations";
import TenantPlan from "../components/TenantPlan";
import SecurityPolicy from "../components/SecurityPolicy";
import PasswordPolicy from "../components/PasswordPolicy";
import AiSettings from "../components/AiSettings";
import DataSecurityNotice from "../components/DataSecurityNotice";
import KeySecurityOnboarding from "../components/KeySecurityOnboarding";
import BrandingSettings from "../components/BrandingSettings";
import BrandingCard from "../components/BrandingCard";
import ModuleSettings from "../components/ModuleSettings";
import AiTransparency from "../components/AiTransparency";
import AiSubmissionLog from "../components/AiSubmissionLog";
import AssignEntitiesDialog, { EntityCheckboxList, regLabel } from "../components/AssignEntitiesDialog";
import LifecycleChecklist from "../components/LifecycleChecklist";
import OffboardingPanel from "../components/OffboardingPanel";

const ROLES = ["admin", "analyst", "reviewer", "auditor"];
const INV_ROLES = [
  { value: "lead_investigator", label: "Lead Investigator" },
  { value: "investigator", label: "Investigator" },
  { value: "investigator_reviewer", label: "Investigator Reviewer" },
  { value: "legal_investigation_rep", label: "Legal Investigation Rep" },
];

const CLEARED_ITEMS = ["Entity Profile", "Documents & Policy Manuals", "Controls", "Evidence (and uploaded files)",
  "Forms & form submissions", "RSAW narratives", "Tasks", "Notifications", "Applicability answers",
  "Requirement role assignments (SME / Compliance)"];
const PRESERVED_ITEMS = ["User accounts & roles", "Standards & Requirements library", "Official RSAW templates", "Audit trail"];

function DataManagement() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [holds, setHolds] = useState([]);

  useEffect(() => { api.get("/legal-holds").then((r) => setHolds(r.data)).catch(() => {}); }, []);

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `claro_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Backup failed"); }
    finally { setBusy(false); }
  };

  const restoreBackup = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data.restored);
      toast.success("Backup restored");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clearData = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clear-entity-data", { confirm });
      setResult(data.cleared); setConfirm("");
      toast.success("All entity data cleared. The workspace is now empty.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="border border-border bg-card p-5" data-testid="backup-restore">
        <div className="font-display font-bold text-sm mb-1">Backup &amp; Restore</div>
        <p className="text-sm text-muted-foreground mb-3">Download a JSON snapshot of all entity data before clearing, so an accidental wipe is recoverable. Restore re-imports a snapshot (replaces current operational data; users &amp; the standards library are untouched).</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="backup-download-btn" onClick={downloadBackup} disabled={busy} variant="outline" className="rounded-none"><Download size={16} className="mr-1" />Download Backup (JSON)</Button>
          <label className="inline-flex items-center gap-1 border border-input bg-card px-3 py-2 text-sm font-medium cursor-pointer hover:bg-secondary/40" data-testid="restore-label">
            <UploadSimple size={16} />Restore from Backup
            <input data-testid="restore-input" type="file" accept="application/json,.json" onChange={restoreBackup} className="hidden" disabled={busy} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-4">
          <div className="font-display font-bold text-sm mb-2">This will permanently delete</div>
          <ul className="text-sm space-y-1 text-muted-foreground list-disc pl-5">
            {CLEARED_ITEMS.map((i) => <li key={i} data-testid={`clear-item-${i}`}>{i}</li>)}
          </ul>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="font-display font-bold text-sm mb-2">This will be preserved</div>
          <ul className="text-sm space-y-1 text-muted-foreground list-disc pl-5">
            {PRESERVED_ITEMS.map((i) => <li key={i}>{i}</li>)}
          </ul>
        </div>
      </div>

      <div className="border-2 border-red-600 bg-red-50 p-5" data-testid="danger-zone">
        <div className="flex items-center gap-2 text-red-700 font-display font-bold"><Warning size={18} weight="fill" />Danger Zone — Clear All Entity Data</div>
        {holds.length > 0 && (
          <div data-testid="hold-protect-note" className="mt-2 border border-amber-500 bg-amber-50 text-amber-800 px-3 py-2 text-sm flex items-center gap-2">
            <Scales size={14} weight="fill" />{holds.length} requirement{holds.length === 1 ? "" : "s"} on Legal Hold — all related data will be skipped (preserved).
          </div>
        )}
        <p className="text-sm text-red-700/90 mt-2">
          This action is irreversible and clears the entire workspace (single-entity app). <span className="font-semibold">Download a backup above first.</span> Type <span className="font-mono font-bold">DELETE</span> below to confirm.
        </p>
        <div className="flex gap-2 mt-3 max-w-md">
          <Input data-testid="clear-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type DELETE" className="rounded-none border-red-400 bg-card" />
          <Button data-testid="clear-data-btn" onClick={clearData} disabled={busy || confirm.trim() !== "DELETE"}
            className="rounded-none bg-red-600 hover:bg-red-700 whitespace-nowrap"><Trash size={16} className="mr-1" />{busy ? "Clearing…" : "Clear Data"}</Button>
        </div>
      </div>

      {result && (
        <div className="border border-border bg-card p-4 text-sm" data-testid="clear-result">
          <div className="font-display font-bold mb-2">Result</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs">
            {Object.entries(result).map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> <span className="font-bold">{v}</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function LegalHolds() {
  const nav = useNavigate();
  const [holds, setHolds] = useState(null);

  const load = () => api.get("/legal-holds").then((r) => setHolds(r.data)).catch(() => setHolds([]));
  useEffect(() => { load(); }, []);

  const release = async (rid) => {
    try {
      await api.post(`/requirements/${rid}/legal-hold`, { active: false, reason: "" });
      toast.success("Legal hold released");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (holds === null) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
        <Scales size={16} weight="fill" className="text-amber-600" />
        <span data-testid="legal-holds-count">{holds.length} requirement{holds.length === 1 ? "" : "s"} currently on legal hold.</span>
      </div>
      {holds.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="legal-holds-empty">
          No active legal holds. Place a hold from any Requirement's detail page.
        </div>
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-amber-50 text-left overline text-amber-800">
              <th className="px-4 py-3">Requirement</th><th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Placed by</th><th className="px-4 py-3">When</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {holds.map((h) => (
                <tr key={h.id} data-testid={`hold-row-${h.id}`} className="border-b border-border align-top">
                  <td className="px-4 py-3">
                    <button onClick={() => nav(`/requirements/${h.id}`)} className="font-mono font-bold hover:underline">{h.standard_id} {h.req_number}</button>
                    <div className="text-xs text-muted-foreground line-clamp-2 max-w-xs mt-0.5">{h.text}</div>
                  </td>
                  <td className="px-4 py-3">{h.legal_hold_reason || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{h.legal_hold_by || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{h.legal_hold_at ? h.legal_hold_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`release-hold-${h.id}`} onClick={() => release(h.id)}
                      className="border border-amber-600 text-amber-700 px-2 py-1 text-xs font-semibold hover:bg-amber-600 hover:text-white whitespace-nowrap">Release</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  const { isRole, user, isSuperadmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "analyst", investigation_role: "none", registration_ids: [] });
  const [regs, setRegs] = useState([]);
  const [activeReg, setActiveReg] = useState(null);
  const [entityUser, setEntityUser] = useState(null);
  const passwordPolicy = usePasswordPolicy();

  const load = () => { api.get("/users").then((r) => setUsers(r.data)); api.get("/my-activity").then((r) => setActivity(r.data.events || [])).catch(() => {}); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/registrations").then((r) => { setRegs(r.data.registrations || []); setActiveReg(r.data.active_registration_id || null); }).catch(() => {});
  }, []);
  const regName = (id) => { const r = regs.find((x) => x.id === id); return r ? regLabel(r) : null; };
  // A tenant admin manages only the entity they're logged into — scope the assign/create
  // pickers to the active entity so users can't be added to (or seen across) other entities.
  const assignableRegs = regs.filter((r) => r.id === activeReg);
  const openNewUser = () => {
    setForm({ name: "", email: "", password: "", role: "analyst", investigation_role: "none",
              registration_ids: activeReg ? [activeReg] : [] });
    setOpen(true);
  };

  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("All fields required");
    if (!passwordValid(form.password, passwordPolicy)) return toast.error("Password doesn't meet the complexity rules.");
    try { await api.post("/users", { ...form, investigation_role: form.investigation_role === "none" ? null : form.investigation_role }); setOpen(false); setForm({ name: "", email: "", password: "", role: "analyst", investigation_role: "none", registration_ids: [] }); load(); toast.success("User created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeClaroAccess = async (u) => {
    if (!window.confirm(`Remove Claro staff member ${u.name || u.email} from this entity? They will no longer be able to open it.`)) return;
    try { await api.delete(`/users/${u.id}/claro-access`); toast.success("Claro staff access removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeRole = async (id, role) => { await api.patch(`/users/${id}`, { role }); load(); toast.success("Role updated"); };
  const changeInvRole = async (id, ir) => { await api.patch(`/users/${id}`, { investigation_role: ir === "none" ? null : ir }); load(); toast.success("Investigation role updated"); };
  const changeBcsi = async (id, val) => { await api.patch(`/users/${id}`, { bcsi_authorized: val }); load(); toast.success(val ? "CIP/BCSI access granted" : "CIP/BCSI access revoked"); };
  const remove = async (id) => { await api.delete(`/users/${id}`); load(); };

  const [sigUser, setSigUser] = useState(null);
  const [sigForm, setSigForm] = useState({ signature_name: "", signature_image: null });
  const openSig = (u) => { setSigUser(u); setSigForm({ signature_name: u.signature_name || "", signature_image: u.signature_image || null }); };
  const pickSig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Image too large (max 1.5 MB).");
    const url = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
    setSigForm((f) => ({ ...f, signature_image: url }));
  };
  const saveSig = async () => {
    try { await api.patch(`/users/${sigUser.id}`, sigForm); setSigUser(null); load(); toast.success("Signature saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fmtLastActive = (u) => {
    if (!u.last_login) return "Never";
    const dt = new Date(u.last_login);
    const when = isNaN(dt) ? (u.last_login || "").slice(0, 16).replace("T", " ") : dt.toLocaleString();
    return `${when}${u.login_count ? ` · ${u.login_count} login${u.login_count > 1 ? "s" : ""}` : ""}`;
  };

  const resetMfa = async (u) => {
    if (!window.confirm(`Reset MFA for ${u.name}? They'll need to set up their authenticator again.`)) return;
    try { await api.post(`/users/${u.id}/mfa-reset`); load(); toast.success("MFA reset"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unlockUser = async (u) => {
    try { await api.post(`/users/${u.id}/unlock`); toast.success(`${u.name} unlocked`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [editBusy, setEditBusy] = useState(false);
  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name || "", email: u.email || "" }); };
  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) return toast.error("Name and email are required.");
    setEditBusy(true);
    try {
      await api.patch(`/users/${editUser.id}`, { name: editForm.name.trim(), email: editForm.email.trim() });
      setEditUser(null); load(); toast.success("User updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  return (
    <div>
      {!isRole("admin") ? (
        <div className="p-8">
          <div className="border border-border bg-card p-10 text-center" data-testid="admin-not-authorized">
            <h2 className="font-display font-bold text-xl">Administrator access required</h2>
            <p className="text-sm text-muted-foreground mt-2">This area is restricted to admin users.</p>
          </div>
        </div>
      ) : (
      <>
      <PageHeader title="Administration" subtitle="Users and roles"
        action={<Button data-testid="new-user-btn" onClick={openNewUser} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New User</Button>} />
      <div className="p-8">
        <Tabs defaultValue="users">
          <TabsList className="rounded-none">
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-none">Users & Roles</TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="rounded-none">Onboarding</TabsTrigger>
            <TabsTrigger value="offboarding" data-testid="tab-offboarding" className="rounded-none">Offboarding</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="rounded-none">Controls &amp; Security</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-none">Branding</TabsTrigger>
            <TabsTrigger value="entity" data-testid="tab-entity" className="rounded-none">Entity Profile</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="tab-registrations" className="rounded-none">Registrations</TabsTrigger>
            {isSuperadmin && <TabsTrigger value="impact" data-testid="tab-impact" className="rounded-none">CIP Impact</TabsTrigger>}
            {isSuperadmin && <TabsTrigger value="rsaw-templates" data-testid="tab-rsaw-templates" className="rounded-none">RSAW Templates</TabsTrigger>}
            <TabsTrigger value="approval-routes" data-testid="tab-approval-routes" className="rounded-none">Approval Routes</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data" className="rounded-none">Data Management</TabsTrigger>
            <TabsTrigger value="retention" data-testid="tab-retention" className="rounded-none">Retention</TabsTrigger>
            <TabsTrigger value="legal-holds" data-testid="tab-legal-holds" className="rounded-none">Legal Holds</TabsTrigger>
            <TabsTrigger value="ai-usage" data-testid="tab-ai-usage" className="rounded-none">AI Usage</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-none">Activity</TabsTrigger>
            <TabsTrigger value="access-report" data-testid="tab-access-report" className="rounded-none">Access Report</TabsTrigger>
          </TabsList>

          <TabsContent value="data">
            <DataManagement />
          </TabsContent>

          <TabsContent value="branding">
            <div className="space-y-6">
              <BrandingCard />
              <BrandingSettings />
            </div>
          </TabsContent>

          <TabsContent value="retention">
            <RetentionManager />
          </TabsContent>

          <TabsContent value="legal-holds">
            <LegalHolds />
          </TabsContent>

          <TabsContent value="entity">
            <EntityProfile />
          </TabsContent>

          <TabsContent value="registrations">
            {isSuperadmin && <TenantPlan />}
            <Registrations />
          </TabsContent>

          {isSuperadmin && <TabsContent value="rsaw-templates">
            <RsawTemplates />
          </TabsContent>}

          <TabsContent value="approval-routes">
            <ApprovalRoutes />
          </TabsContent>

          {isSuperadmin && <TabsContent value="impact">
            <CipImpact />
          </TabsContent>}

          <TabsContent value="users">
            <ClaroStaffInvites />
            <div className="border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Claro ID</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Entities</th><th className="px-4 py-3">Last active</th><th className="px-4 py-3">MFA</th><th className="px-4 py-3">Signature</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-border${u.is_active === false ? " opacity-70" : ""}`}>
                      <td className="px-4 py-3 font-medium">
                        <span>{u.name}</span>
                        {u.is_superadmin && (
                          <span className="ml-2 inline-flex items-center gap-1.5 align-middle">
                            <span className="border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-700" data-testid={`claro-staff-badge-${u.id}`}>Claro staff</span>
                            {u.id !== user?.id && <button onClick={() => removeClaroAccess(u)} className="text-[10px] text-red-600 underline" data-testid={`remove-claro-access-${u.id}`}>Remove access</button>}
                          </span>
                        )}
                        {u.is_active === false && <span data-testid={`inactive-badge-${u.id}`} className="ml-2 inline-block border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-destructive align-middle">Inactive</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground" data-testid={`user-claroid-${u.id}`}>{u.claro_id || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3">
                        <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                          <SelectTrigger data-testid={`role-select-${u.id}`} className="rounded-none w-36 capitalize"><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={u.investigation_role || "none"} onValueChange={(v) => changeInvRole(u.id, v)}>
                          <SelectTrigger data-testid={`inv-role-select-${u.id}`} className="rounded-none w-44 mt-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No investigation role</SelectItem>
                            {INV_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <label className="mt-2 flex items-center gap-1.5 text-xs" data-testid={`bcsi-toggle-wrap-${u.id}`}
                          title="Grants access to the CIP Workspace and BCSI-bearing data">
                          <input type="checkbox" data-testid={`bcsi-toggle-${u.id}`}
                            checked={u.role === "admin" || !!u.bcsi_authorized}
                            disabled={u.role === "admin"}
                            onChange={(e) => changeBcsi(u.id, e.target.checked)} />
                          <span className={u.role === "admin" ? "text-muted-foreground" : ""}>CIP / BCSI access{u.role === "admin" ? " (admin)" : ""}</span>
                        </label>
                      </td>
                      <td className="px-4 py-3" data-testid={`user-entities-${u.id}`}>
                        <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                          {(u.allowed_registration_ids || []).map((rid) => regName(rid)).filter(Boolean).sort((a, b) => a.localeCompare(b)).map((nm, i) => (
                            <span key={i} className="inline-block border border-border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium">{nm}</span>
                          ))}
                          {(() => { const unknown = (u.allowed_registration_ids || []).filter((rid) => !regName(rid)).length; return unknown ? <span className="text-[10px] text-muted-foreground">+{unknown} other</span> : null; })()}
                          {!(u.allowed_registration_ids || []).length && <span className="text-[10px] text-muted-foreground italic">none</span>}
                          <button data-testid={`edit-entities-${u.id}`} onClick={() => setEntityUser(u)} className="text-[10px] text-primary underline ml-1">Edit</button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs" data-testid={`last-active-${u.id}`}>
                        <span className={u.last_login ? "text-foreground" : "text-muted-foreground italic"}>{fmtLastActive(u)}</span>
                      </td>
                      <td className="px-4 py-3" data-testid={`mfa-status-${u.id}`}>
                        {u.mfa_enabled
                          ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-green-700"><ShieldCheck size={13} weight="fill" />On</span>
                          : <span className="text-[11px] font-semibold uppercase text-muted-foreground">Off</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button data-testid={`manage-sig-${u.id}`} onClick={() => openSig(u)} className="inline-flex items-center gap-2 border border-border px-2 py-1 text-xs hover:bg-secondary">
                          {u.signature_image ? <img src={u.signature_image} alt="sig" className="h-5 object-contain" /> : <PenNib size={14} />}
                          {u.signature_name ? <span className="font-medium">{u.signature_name}</span> : <span className="text-muted-foreground">Set signature</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} title="Edit name / email & view history" className="text-xs text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                          <button data-testid={`unlock-${u.id}`} onClick={() => unlockUser(u)} title="Clear login lockout" className="text-xs text-muted-foreground hover:text-foreground">Unlock</button>
                          {u.mfa_enabled && <button data-testid={`reset-mfa-${u.id}`} onClick={() => resetMfa(u)} title="Reset MFA" className="text-xs text-amber-600 hover:text-amber-700">Reset MFA</button>}
                          <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} title="Delete user" className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="onboarding" className="mt-6">
            <div className="border border-border bg-card p-6">
              <LifecycleChecklist type="onboarding" title="Getting started"
                blurb="Work through these steps to get your entity fully set up in ClaroShield." />
            </div>
          </TabsContent>

          <TabsContent value="offboarding" className="mt-6">
            <OffboardingPanel />
          </TabsContent>

          <TabsContent value="security">
            <div className="space-y-6">
              <div data-testid="trust-overview-card" className="rounded-xl border border-[#071B3E] bg-[#071B3E] text-white p-5">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-white/10 flex items-center justify-center">
                    <ShieldCheck size={20} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">Trust &amp; Security Overview (PDF)</h3>
                    <p className="text-[13px] text-white/70 mt-0.5 leading-snug">
                      A plain-language, co-branded summary you can share with your own auditors or stakeholders —
                      how your data is kept separate from other customers, encrypted in transit and at rest, and
                      who can access it, plus data retention &amp; backups.
                    </p>
                    <Button size="sm" data-testid="trust-overview-download"
                            onClick={async () => {
                              try { await downloadBlob("/soc2/trust-overview.pdf", "Trust_Security_Overview.pdf"); }
                              catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
                            }}
                            className="mt-3 h-9 bg-white text-[#071B3E] hover:bg-slate-100">
                      <Download size={15} className="mr-1.5" /> Download Trust &amp; Security Overview
                    </Button>
                  </div>
                </div>
              </div>
              <DataSecurityNotice />
              <KeySecurityOnboarding />
              <AiSettings />
              <AiTransparency />
              <AiSubmissionLog />
              <ModuleSettings />
              <SecurityPolicy />
              <PasswordPolicy />
            </div>
          </TabsContent>

          <TabsContent value="ai-usage">
            <AiUsageReport />
          </TabsContent>

          <TabsContent value="access-report">
            <AccessReport />
          </TabsContent>
          <TabsContent value="activity">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">A read-only record of activity for your organization only. Most recent 300 events.</p>
              <Button data-testid="export-activity-btn" variant="outline" className="rounded-none"
                      onClick={async () => { try { await downloadBlob("/my-activity/export", `activity_log_${new Date().toISOString().slice(0,10)}.xlsx`); } catch (e) { toast.error(e.message || "Export failed."); } }}>
                <Download size={16} className="mr-1" />Export Excel
              </Button>
            </div>
            <div className="border border-border bg-card max-h-[60vh] overflow-y-auto" data-testid="tenant-activity-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0"><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground"><th className="px-4 py-3">Time (UTC)</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th><th className="px-4 py-3">Details</th></tr></thead>
                <tbody>
                  {activity.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground text-center" data-testid="tenant-activity-empty">No activity recorded yet for your organization.</td></tr>
                  )}
                  {activity.map((l) => (
                    <tr key={l.id} data-testid="tenant-activity-row" className="border-b border-border">
                      <td className="px-4 py-2 font-mono text-xs">{l.timestamp?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-2">{l.user_name}</td>
                      <td className="px-4 py-2 capitalize">{(l.action || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 capitalize">{l.entity_type}</td>
                      <td className="px-4 py-2 text-muted-foreground">{l.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Password</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none font-mono" />
                <Button type="button" variant="outline" className="rounded-none whitespace-nowrap" onClick={() => setForm({ ...form, password: generatePassword() })}>Generate</Button>
              </div>
              <PasswordChecklist pw={form.password} className="mt-2" />
            </div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none mt-1 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Investigation role <span className="text-muted-foreground font-normal">(grants Investigations access)</span></Label>
              <Select value={form.investigation_role} onValueChange={(v) => setForm({ ...form, investigation_role: v })}>
                <SelectTrigger data-testid="user-inv-role-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {INV_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Entities <span className="text-muted-foreground font-normal">(assign to one or more)</span></Label>
              <div className="mt-1">
                <EntityCheckboxList registrations={assignableRegs} value={form.registration_ids}
                  onChange={(ids) => setForm({ ...form, registration_ids: ids })} testidPrefix="new-user-entity" />
              </div>
            </div>
            <Button data-testid="create-user-btn" onClick={create} className="rounded-none w-full bg-primary hover:bg-primary/90">Create User</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sigUser} onOpenChange={(o) => !o && setSigUser(null)}>
        <DialogContent className="rounded-none" data-testid="signature-dialog">
          <DialogHeader><DialogTitle>Signature — {sigUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Set a reusable signature for this user. They can apply it (or type a fresh one) when signing documents.</p>
            <div><Label>Typed signature (name)</Label>
              <Input data-testid="sig-name-input" value={sigForm.signature_name} onChange={(e) => setSigForm({ ...sigForm, signature_name: e.target.value })} className="rounded-none mt-1" placeholder="e.g. Jane A. Smith" /></div>
            <div><Label>Signature image (optional)</Label>
              <div className="flex items-center gap-2 mt-1">
                <label className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-xs cursor-pointer hover:bg-secondary">
                  <UploadSimple size={14} />Upload
                  <input data-testid="sig-image-input" type="file" accept="image/*" className="hidden" onChange={pickSig} />
                </label>
                {sigForm.signature_image && <img src={sigForm.signature_image} alt="sig" className="h-9 object-contain border border-border" />}
                {sigForm.signature_image && <button onClick={() => setSigForm({ ...sigForm, signature_image: null })} className="text-xs text-red-600 underline">remove</button>}
              </div>
            </div>
            <Button data-testid="save-sig-btn" onClick={saveSig} className="rounded-none w-full bg-primary hover:bg-primary/90">Save signature</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-none" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user — {editUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between border border-border bg-secondary/30 px-3 py-2">
              <span className="text-xs text-muted-foreground uppercase overline">Claro ID</span>
              <span className="font-mono text-sm" data-testid="edit-user-claroid">{editUser?.claro_id || "—"}</span>
            </div>
            <div><Label>Name</Label><Input data-testid="edit-user-name-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="edit-user-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-none mt-1" /></div>
            <div>
              <Label className="flex items-center gap-1.5"><ClockCounterClockwise size={14} />Change history</Label>
              <div className="mt-1 border border-border max-h-44 overflow-y-auto" data-testid="edit-user-history">
                {(editUser?.change_history || []).length === 0
                  ? <p className="px-3 py-2 text-xs text-muted-foreground italic">No changes recorded yet.</p>
                  : [...(editUser?.change_history || [])].reverse().map((h, i) => (
                      <div key={i} className="border-b border-border last:border-0 px-3 py-2 text-xs">
                        <span className="font-medium capitalize">{h.field}</span>{" "}
                        <span className="text-muted-foreground">changed from</span>{" "}
                        <span className="font-mono">{h.old || "—"}</span>{" "}
                        <span className="text-muted-foreground">to</span>{" "}
                        <span className="font-mono">{h.new || "—"}</span>
                        <div className="text-[10px] text-muted-foreground mt-0.5">by {h.changed_by || "—"} · {(h.at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                    ))}
              </div>
            </div>
            <Button data-testid="save-edit-user-btn" disabled={editBusy} onClick={saveEdit} className="rounded-none w-full bg-primary hover:bg-primary/90">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
      <AssignEntitiesDialog open={!!entityUser} onOpenChange={(o) => !o && setEntityUser(null)}
        user={entityUser} registrations={assignableRegs} onSaved={load} />
      </>
      )}
    </div>
  );
}
